import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { prop } from 'ramda';
import { dispatcher } from 'dispatcher';
import Form from 'js/components/common/Form';
import { clsx } from 'js/utils/classNames.util';
import { hideKeyboard } from 'js/utils/DOM.util';
import { Button } from 'js/components/common/Button';
import { ReCaptcha } from 'js/components/main/ReCaptcha';
import { Input } from 'js/components/common/Fields/Input';
import { useGetErrorMessage } from 'js/hooks/server/hooks';
import { useLoginByCredentials } from 'js/hooks/auth/hooks';
import { useComponentTranslations } from 'js/hooks/component';
import { Checkbox } from 'js/components/common/Fields/Checkbox';
import { withComponent } from 'js/components/hocs/withComponent';
import { componentService } from 'js/services/component.service';
import { ErrorMessage } from 'js/components/common/ErrorMessage';
import { useAppSelector, useUserSelector } from 'js/store/hooks';
import { useGetLoginMode } from 'js/hooks/auth/hooks/useGetLoginMode';
import { userSetAttributesAction } from 'js/hooks/user/store/actions';
import { setIgnoreLogoutNotification } from 'js/components/system/App/store/actions';

import * as hooks from './hooks';
import * as Types from './types';

import style from './Login.scss';

export const LoginBare = ({
    autoFocus,
    className,
    emailRegexp
}: {
    autoFocus?: boolean;
    className?: string;
    emailRegexp?: string;
}) => {
    const dispatch = useDispatch();
    const getLoginMode = useGetLoginMode();
    const translate = useComponentTranslations();
    const getErrorMessage = useGetErrorMessage();
    const usernameRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const isUsernameInUppercase = useAppSelector(prop('isUsernameInUppercase'));
    const loginByCredentials = useLoginByCredentials();

    const {
        isLoaded,
        loadingError,
        isRememberMeSupported,
        isRememberDeviceSupported,
        isFitToPlaySubmissionRequired
    } = hooks.useInitLogin();

    useEffect(() => {
        dispatch(setIgnoreLogoutNotification(true));

        return () => {
            dispatch(setIgnoreLogoutNotification(false));
        };
    }, [dispatch]);

    const {
        reCaptchaError,
        isReCaptchaVisible,
        onReCaptchaError,
        onReCaptchaValidate,
        showReCaptcha
    } = hooks.useReCaptcha();

    const validationConfig = hooks.useValidationConfig();

    const onLoginSubmit = useCallback(async (formData: Types.ILoginForm) => {
        if (isReCaptchaVisible) {
            return translate('playtech.mobile.system.recaptcha.error.captchaNotPassed');
        }

        try {
            const { userName: formUserName, password, fitToPlay, rememberMe, rememberDevice } = formData;

            dispatch(userSetAttributesAction({ rememberDevice }));

            const userName = isUsernameInUppercase ? formUserName.toUpperCase() : formUserName;
            const loginMode = getLoginMode(formUserName, emailRegexp);

            await loginByCredentials(
                userName,
                password,
                rememberMe,
                false,
                loginMode,
                fitToPlay
            );

            hideKeyboard();
            dispatcher.dispatch('page:refresh');
        } catch (error: any) {
            const response = error?.response || error || {};
            const { data: { errorCode, isPrevented } = {} } = response;

            if (!response.data || isPrevented || errorCode === 0) {
                // isPrevented - login will be handled by native app
                return;
            }

            if (errorCode === 301) {
                showReCaptcha();

                return;
            }

            passwordRef.current?.focus();

            return getErrorMessage(error);
        }
    }, [
        dispatch,
        emailRegexp,
        getErrorMessage,
        getLoginMode,
        isReCaptchaVisible,
        isUsernameInUppercase,
        loginByCredentials,
        showReCaptcha,
        translate
    ]);

    useEffect(() => {
        if (isLoaded && autoFocus && usernameRef.current) {
            // waits for app to be fully loaded and preloader to disappear.
            // otherwise component will be rendered but not visible due to "hidden" class on all app container and focus won't work.
            const observer = new IntersectionObserver(([entry]) => {
                if (entry.isIntersecting) {
                    usernameRef.current?.focus();

                    observer.disconnect();
                }
            });

            observer.observe(usernameRef.current);

            return () => {
                observer.disconnect();
            };
        }
    }, [isLoaded, autoFocus]);

    if (isLoggedIn) {
        return null;
    }

    return (
        <div className={clsx([style.container, className, 'cms-login'])}>
            <Form<Types.ILoginForm>
                onSubmit={onLoginSubmit}
                validationConfig={validationConfig}
                render={({ submitting, handleSubmit, submitError }) => {
                    const error = loadingError || reCaptchaError || submitError;

                    return (
                        <form className={style.form} onSubmit={handleSubmit}>
                            {error && <ErrorMessage className={style.error} error={error} animate={true} />}
                            <div className={style.inputFields}>
                                <Input
                                    aria-label={translate(['loginname.label', 'playtech.mobile.system.input.aria-label'])}
                                    autoCapitalize="none"
                                    autoComplete="off"
                                    autoCorrect="off"
                                    className={clsx([
                                        style.field,
                                        style.inputField,
                                        isUsernameInUppercase && style.uppercase
                                    ])}
                                    disabled={!isLoaded}
                                    inputRef={usernameRef}
                                    label={translate('loginname.label')}
                                    name="userName"
                                    placeholder={translate('loginname')}
                                    spellCheck="false"
                                    type="text"
                                />
                                <Input
                                    aria-label={translate(['password.label', 'playtech.mobile.system.input.aria-label'])}
                                    autoComplete="off"
                                    className={clsx([style.field, style.inputField])}
                                    disabled={!isLoaded}
                                    inputRef={passwordRef}
                                    label={translate('password.label')}
                                    name="password"
                                    placeholder={translate('password')}
                                    type="password"
                                />
                            </div>
                            {isRememberMeSupported && (
                                <Checkbox
                                    aria-label={translate(['rememberMe', 'playtech.mobile.system.checkbox.aria-label'])}
                                    className={style.field}
                                    label={translate('rememberMe')}
                                    name="rememberMe"
                                />
                            )}
                            {isRememberDeviceSupported && (
                                <Checkbox
                                    aria-label={translate(['rememberDevice', 'playtech.mobile.system.checkbox.aria-label'])}
                                    className={style.field}
                                    label={translate('rememberDevice')}
                                    name="rememberDevice"
                                />
                            )}
                            {isFitToPlaySubmissionRequired && (
                                <Checkbox
                                    aria-label={translate(['fitToPlay', 'playtech.mobile.system.checkbox.aria-label'])}
                                    className={style.field}
                                    label={translate('fitToPlay')}
                                    name="fitToPlay"
                                />
                            )}
                            <div className={style.recaptcha}>
                                {isReCaptchaVisible && (
                                    <ReCaptcha
                                        operation="login"
                                        onError={onReCaptchaError}
                                        onValidate={onReCaptchaValidate}
                                    />
                                )}
                            </div>
                            <div className={`${style.controls} cms-login-controls`}>
                                <Button
                                    disabled={submitting || !isLoaded}
                                    isNew
                                    size="large"
                                    type="submit"
                                >
                                    {translate('button.login')}
                                </Button>
                            </div>
                        </form>
                    );
                }}
            />
        </div>
    );
};

export const Login = withComponent({
    tkPrefix: 'playtech.mobile.system.login.'
})(LoginBare);

/**
 * Adds the login form.
 * Use email_regexp to change default email validation rule.
 * Use auto_focus='true' to enable username autofocus
 * Usage:
 * <hub2-login></hub2-login>
 * <hub2-login email_regexp="[a-zA-Z-_.]+@([a-zA-Z-]+\.[a-zA-Z-]+)+"></hub2-login>
 * <hub2-login auto_focus="true"></hub2-login>
 */
componentService.registerWebContentComponents({ Login });
